import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useRemoveMultipleParams } from "../../../hooks/useRemoveMultipleParams";
import { useSetMultipleParams } from "../../../hooks/useSetMultipleParams";
import { addAllBoard } from "../../../redux/SearchSlice";
import { useResponsive } from "../../../hooks/useResponsive";

export function useMyTaskFilterState({ onApply }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const reporter = searchParams.get("reporter");
  const [reporterValue, setReporterValue] = React.useState(reporter === "true");
  const [setMultipleSearchParams] = useSetMultipleParams();
  const [removeMultiParams] = useRemoveMultipleParams();
  const isMobile = useResponsive("down", "md");
  const [oldBoard, , ] = useCustomSearchParams("board");
  const [milestones] = useCustomSearchParams("milestone");
  const checkedList = useSelector((state) => state.search.boardSearch);
  const selectedEpic = useSelector((state) => state.search.epicSearch);

  const removeSearchParams = React.useCallback(
    (paramsToRemove) => {
      const newSearchParams = new URLSearchParams(searchParams);
      paramsToRemove.forEach((param) => {
        newSearchParams.delete(param);
      });
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchParams = React.useCallback(
    (key, value) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(key, value);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = React.useCallback(() => {
    removeSearchParams(["board", "milestone", "reporter"]);
    dispatch(addAllBoard([]));
  }, [dispatch, removeSearchParams]);

  const changeReporterSearch = React.useCallback(
    (event) => {
      event.stopPropagation();
      setReporterValue((prev) => !prev);

      if (!isMobile) {
        if (reporter === "true") {
          removeSearchParams(["reporter"]);
        } else {
          updateSearchParams("reporter", true);
        }
      }
    },
    [isMobile, removeSearchParams, reporter, updateSearchParams]
  );

  const handleApplyFilter = React.useCallback(() => {
    if (checkedList.length === 0 && oldBoard?.length > 0 && !reporterValue) {
      removeMultiParams(["board", "milestone", "reporter"]);
    } else if (
      checkedList.length > 1 &&
      selectedEpic.length === 0 &&
      milestones?.length > 0 &&
      !reporterValue
    ) {
      removeSearchParams(["milestone", "reporter"]);
    } else if (
      selectedEpic.length > 0 &&
      checkedList.length === 1 &&
      reporterValue
    ) {
      setMultipleSearchParams([
        { name: "milestone", value: selectedEpic },
        { name: "board", value: checkedList },
        { name: "reporter", value: true },
      ]);
    } else if (reporterValue) {
      setMultipleSearchParams([
        { name: "board", value: checkedList },
        { name: "reporter", value: true },
      ]);
    } else {
      setMultipleSearchParams([
        { name: "milestone", value: selectedEpic },
        { name: "board", value: checkedList },
        { name: "reporter", value: false },
      ]);
    }

    onApply?.();
  }, [
    checkedList,
    milestones,
    oldBoard,
    onApply,
    removeMultiParams,
    removeSearchParams,
    reporterValue,
    selectedEpic,
    setMultipleSearchParams,
  ]);

  return {
    isMobile,
    reporter,
    reporterValue,
    checkedList,
    oldBoard,
    clearFilters,
    changeReporterSearch,
    handleApplyFilter,
  };
}
